import { Suspense, lazy } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useMaintenance } from '@/context/MaintenanceContext.jsx';
import { useAuth } from '@/context/AuthContext.jsx';
import {
  isMaintenanceAllowedPath,
  isMaintenancePrivilegedUser,
} from '@/lib/maintenanceAccess.js';

const MaintenancePage = lazy(() => import('@/pages/Maintenance.jsx'));

const FullScreenLoader = () => (
  <div className="flex min-h-screen items-center justify-center bg-slate-950 text-slate-200">
    <div className="flex items-center gap-3 text-lg font-medium">
      <Loader2 className="h-5 w-5 animate-spin text-indigo-400" />
      <span>Caricamento...</span>
    </div>
  </div>
);

function MaintenanceGate({ children }) {
  const location = useLocation();
  const { user, isLoading: authLoading } = useAuth();
  const {
    isEffectiveMaintenance,
    message,
    allowedPaths,
    allowedRoles,
    isLoading: maintenanceLoading,
  } = useMaintenance();

  if (maintenanceLoading) {
    return <FullScreenLoader />;
  }

  const currentPath = location.pathname || '/';
  const isAllowedPath = isMaintenanceAllowedPath(currentPath, allowedPaths);

  // Once maintenance state is known and inactive, auth hydration is not on the critical path.
  if (!isEffectiveMaintenance) {
    return children;
  }

  // During active maintenance, routes explicitly allowed by config can render immediately
  // even if auth is still hydrating because privilege checks are not needed for them.
  if (authLoading) {
    return isAllowedPath ? children : <FullScreenLoader />;
  }

  const isPrivilegedUser = isMaintenancePrivilegedUser(user, allowedRoles);

  if (!isPrivilegedUser && !isAllowedPath) {
    return (
      <Suspense fallback={<FullScreenLoader />}>
        <MaintenancePage message={message} />
      </Suspense>
    );
  }

  return children;
}

export default MaintenanceGate;
